import { useMarkRouteLoading } from '@/components/route-loading-announcer';
import { Skeleton } from '@/components/ui';
import { cn } from '@/helpers/ui-utils';

interface RouteSkeletonProps {
  /**
   * Fade in over 120ms when the skeleton appears. Turn it off for a boundary
   * that usually takes over from a skeleton already on screen, where replaying
   * the fade would flash.
   */
  fadeIn?: boolean;
}

/**
 * Stands in for a page while its code loads. It's the fallback for the
 * route-level Suspense boundaries: all routed content in `App.tsx`, and the
 * course pages in `pages/course-visit.tsx` and `pages/courses.tsx`. Its
 * container mirrors `CourseCatalog.module.css`, since `/courses` is the
 * default route, so the most common transition keeps its width.
 *
 * Neutral greys only: on a partner hostname it can render before or after that
 * partner's branding loads, and has to look the same either way.
 *
 * Hidden from screen readers. It reports itself to `RouteLoadingAnnouncer`,
 * which says "Loading…" if the page is slow to arrive.
 */
export default function RouteSkeleton({ fadeIn = true }: RouteSkeletonProps) {
  useMarkRouteLoading();

  return (
    <div
      aria-hidden="true"
      data-testid="route-skeleton"
      className={cn(
        'mx-4 my-10 min-[900px]:mx-8 min-[900px]:my-12',
        fadeIn &&
          'motion-safe:animate-in motion-safe:fade-in motion-safe:duration-[120ms]',
      )}
    >
      <div className="mx-auto flex max-w-[400px] flex-col gap-8 px-4 min-[900px]:max-w-[1100px] min-[900px]:px-8 min-[1200px]:max-w-[1152px]">
        <Skeleton className="h-5 w-2/5" />
        <div className="grid gap-8 min-[900px]:grid-cols-2">
          <TilePlaceholder />
          <TilePlaceholder />
        </div>
      </div>
    </div>
  );
}

/** The outline of a course tile: an image, a few lines of text, a button. */
function TilePlaceholder() {
  return (
    <div className="flex flex-col gap-4 rounded-[7px] border border-[var(--border-color)] bg-white p-6 min-[900px]:flex-row">
      <Skeleton className="h-20 w-full min-[900px]:h-24 min-[900px]:w-24 min-[900px]:shrink-0" />
      <div className="flex flex-1 flex-col gap-3">
        <Skeleton className="h-3 w-4/5" />
        <Skeleton className="h-3 w-3/5" />
        <Skeleton className="h-3 w-2/5" />
        <Skeleton className="mt-2 h-8 w-32 rounded-full" />
      </div>
    </div>
  );
}
